import { backendClient } from '../lib/api/backendClient';

export interface FavoriteComposer {
  id: string;
  composerId: string;
  savedAt: string;
  name?: string;
  imageUrl?: string;
  uri?: string;
  era?: string;
  birthYear?: string;
  deathYear?: string;
  spotifyArtistId?: string;
  notes?: string;
}

const toIsoString = (value?: string | Date): string | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value.toISOString();
  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf()) ? undefined : parsed.toISOString();
};

const deserializeFavoriteComposer = (payload: any): FavoriteComposer => {
  const stringOrUndefined = (value: unknown): string | undefined =>
    typeof value === 'string' && value.length ? value : undefined;

  const savedAt = typeof payload?.savedAt === 'string' ? payload.savedAt : undefined;
  const fallback =
    (typeof payload?.updated_at === 'string' && payload.updated_at) ||
    (typeof payload?.created_at === 'string' && payload.created_at) ||
    new Date().toISOString();

  const composerId =
    stringOrUndefined(payload?.composerId) ??
    stringOrUndefined(payload?.composer_id) ??
    stringOrUndefined(payload?.id) ??
    '';

  return {
    id: stringOrUndefined(payload?.id) ?? composerId,
    composerId,
    notes: typeof payload?.notes === 'string' ? payload.notes : undefined,
    name: stringOrUndefined(payload?.name),
    imageUrl: stringOrUndefined(payload?.imageUrl) ?? stringOrUndefined(payload?.image_url),
    uri: stringOrUndefined(payload?.uri),
    era: stringOrUndefined(payload?.era),
    birthYear: stringOrUndefined(payload?.birthYear) ?? stringOrUndefined(payload?.birth_year),
    deathYear: stringOrUndefined(payload?.deathYear) ?? stringOrUndefined(payload?.death_year),
    spotifyArtistId:
      stringOrUndefined(payload?.spotifyArtistId) ?? stringOrUndefined(payload?.spotify_artist_id),
    savedAt: toIsoString(savedAt) ?? fallback,
  };
};

export const favoriteComposerService = {
  async getFavoriteComposers(): Promise<FavoriteComposer[]> {
    const { data } = await backendClient.get<{ composers?: any[] }>('/favorites/composers');
    const composers = Array.isArray(data?.composers) ? data?.composers : [];
    return composers.map(deserializeFavoriteComposer);
  },

  async getFavoriteComposer(composerId: string): Promise<FavoriteComposer | null> {
    if (!composerId) return null;
    try {
      const { data } = await backendClient.get<{ composer?: any }>(
        `/favorites/composers/${encodeURIComponent(composerId)}`
      );
      if (!data?.composer) return null;
      return deserializeFavoriteComposer(data.composer);
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      throw error;
    }
  },

  async saveFavoriteComposer(composerId: string, options?: { notes?: string; savedAt?: string | Date }) {
    const payload: Record<string, unknown> = { composerId };
    if (options?.notes) payload.notes = options.notes;
    if (options?.savedAt) payload.savedAt = toIsoString(options.savedAt);

    const { data } = await backendClient.post<{ composer: any }>('/favorites/composers', payload);
    return deserializeFavoriteComposer(data.composer);
  },

  async updateFavoriteComposer(composerId: string, options?: { notes?: string; savedAt?: string | Date }) {
    const payload: Record<string, unknown> = {};
    if (options?.notes) payload.notes = options.notes;
    if (options?.savedAt) payload.savedAt = toIsoString(options.savedAt);

    const { data } = await backendClient.put<{ composer: any }>(
      `/favorites/composers/${encodeURIComponent(composerId)}`,
      payload
    );
    return deserializeFavoriteComposer(data.composer);
  },

  async removeFavoriteComposer(composerId: string): Promise<void> {
    await backendClient.delete(`/favorites/composers/${encodeURIComponent(composerId)}`);
  },
};
